import AdminLogin from '../components/AdminLogin';
import { useAuth } from '../contexts/AuthContext';
// src/pages/PhotosPage.jsx
import React, { useState, useEffect } from 'react';
import { Camera, Lock, X, LogIn, Film } from 'lucide-react';
import { photoUtils } from '../lib/photoUtils';
import PhotoModal from '../components/PhotoModal';
import PhotoCard from '../components/PhotoCard';
import PhotoUploadForm from '../components/PhotoUploadForm';


const PhotosPage = () => {
    const [media, setMedia] = useState([]);
    const [videos, setVideos] = useState([]);
    const [view, setView] = useState('photos'); // 'photos' | 'videos'
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const { isAdmin, logout } = useAuth();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    const categories = ['all', 'Events', 'Education', 'Health', 'Community', 'Projects', 'ABYETS'];

    useEffect(() => {
        loadMedia();
    }, []);

    const loadMedia = async () => {
        try {
            setLoading(true);
            setError(null);
            const [fetchedPhotos, fetchedVideos] = await Promise.all([
                photoUtils.getPhotos(),
                photoUtils.getVideos(),
            ]);
            setMedia(fetchedPhotos);
            setVideos(fetchedVideos);
        } catch (error) {
            console.error('Error loading media:', error);
            setError(error.message || 'Failed to load media');
        } finally {
            setLoading(false);
        }
    };

    const handleMediaSubmit = async (mediaData) => {
        try {
            setError(null);
            await photoUtils.createPhoto(mediaData);
            await loadMedia();
        } catch (error) {
            console.error('Error creating media:', error);
            throw error;
        }
    };

    const handleMediaDelete = async (mediaId, fileId) => {
        try {
            setError(null);
            await photoUtils.deletePhoto(mediaId, fileId);
            await loadMedia();
        } catch (error) {
            console.error('Error deleting media:', error);
            alert('Failed to delete media');
        }
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
    };

    const handleLogout = () => logout();
    


    const activeMedia = view === 'videos' ? videos : media;
    const filteredMedia = selectedCategory === 'all'
        ? activeMedia
        : activeMedia.filter(item => item.category === selectedCategory);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
                    <p className="text-gray-600">Loading media...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero */}
            <header className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-purple-950 pt-32 pb-24">
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -top-10 left-1/4 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-gold-400/10 blur-3xl" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10
                                     px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-purple-100 backdrop-blur-sm">
                        <Camera className="h-4 w-4" />
                        Gallery
                    </span>
                    <h1 className="mt-5 font-display text-5xl sm:text-6xl font-semibold text-white">
                        Moments &amp;{' '}
                        <span className="bg-gradient-to-r from-gold-200 to-white bg-clip-text italic text-transparent">
                            Memories
                        </span>
                    </h1>
                    <p className="mx-auto mt-5 max-w-2xl text-lg text-purple-100/80">
                        A living archive of our community in action — events, programs, and the
                        people who make it all possible.
                    </p>

                    {/* Photos / Videos toggle */}
                    <div className="mx-auto mt-8 inline-flex rounded-full border border-white/15 bg-white/10 p-1 backdrop-blur-sm">
                        {[
                            { id: 'photos', label: 'Photos', Icon: Camera, count: media.length },
                            { id: 'videos', label: 'Videos', Icon: Film, count: videos.length },
                        ].map(({ id, label, Icon, count }) => (
                            <button
                                key={id}
                                onClick={() => { setView(id); setSelectedCategory('all'); }}
                                className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold
                                         transition-all duration-300 ${
                                    view === id
                                        ? 'bg-white text-purple-800 shadow'
                                        : 'text-white/80 hover:text-white'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                {label}
                                {count > 0 && (
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                                        view === id ? 'bg-purple-100 text-purple-700' : 'bg-white/15 text-white/90'
                                    }`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {/* Category Filter - Sticky */}
            <div className="sticky top-24 z-40 border-b border-gray-200 bg-white/90 shadow-sm backdrop-blur">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex overflow-x-auto gap-2 no-scrollbar">
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold
                                             transition-all duration-200 ${
                                        selectedCategory === category
                                            ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                                            : 'bg-gray-100 text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                                    }`}
                                >
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </button>
                            ))}
                        </div>

                        {isAdmin && (
                            <div className="flex flex-shrink-0 gap-2">
                                <button
                                    onClick={handleLogout}
                                    className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-red-600
                                             transition-colors hover:bg-red-50 hover:text-red-700"
                                >
                                    Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 py-8">
                {/* Admin Upload Form */}
                {isAdmin && <PhotoUploadForm onSubmit={handleMediaSubmit} />}

                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl">
                        <p className="text-red-600">{error}</p>
                    </div>
                )}

                {/* Media Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
                    {filteredMedia.length > 0 ? (
                        filteredMedia.map((item) => (
                            <PhotoCard
                                key={item.$id}
                                photo={item}
                                onClick={setSelectedItem}
                                isAdmin={isAdmin}
                                onDelete={handleMediaDelete}
                            />
                        ))
                    ) : (
                        <div className="col-span-full">
                            <div className="mx-auto max-w-md rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
                                <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-purple-50">
                                    <Camera className="h-8 w-8 text-purple-400" />
                                </div>
                                <h3 className="font-display text-xl font-semibold text-gray-900 mb-2">
                                    {selectedCategory === 'all'
                                        ? 'No Media Yet'
                                        : `No Media in ${selectedCategory}`}
                                </h3>
                                <p className="text-gray-600">
                                    {selectedCategory === 'all'
                                        ? 'Upload some photos or videos to get started.'
                                        : `Try selecting a different category or upload new content.`}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Admin Login Button */}
            {!isAdmin && (
                <button
                    onClick={() => setShowLoginModal(true)}
                    className="fixed bottom-6 right-6 w-12 h-12 bg-white/90 backdrop-blur rounded-full
                             shadow-lg hover:shadow-xl border border-gray-200/50 flex items-center
                             justify-center transition-all duration-200 hover:-translate-y-1
                             hover:bg-purple-50 hover:border-purple-200 z-50"
                    aria-label="Admin Login"
                >
                    <Lock className="w-5 h-5 text-gray-600" />
                </button>
            )}

            {/* Modals */}
            {showLoginModal && <AdminLogin onClose={() => setShowLoginModal(false)} />}

            {selectedItem && (
                <PhotoModal
                    photo={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    isAdmin={isAdmin}
                    onDelete={handleMediaDelete}
                />
            )}
        </div>
    );
};

export default PhotosPage;